import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { GraduationCap, Users, Award, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalCourses: 0,
    totalStudents: 0,
    totalCertificates: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Redirigir alumnos al StudentDashboard
    if (user?.role === 'Alumno') {
      navigate('/student-dashboard', { replace: true });
      return;
    }
    fetchStats();
  }, [user, navigate]);

  const fetchStats = async () => {
    try {
      const [coursesRes, studentsRes] = await Promise.all([
        axios.get(`${API_URL}/courses`),
        axios.get(`${API_URL}/students`)
      ]);

      setStats({
        totalCourses: coursesRes.data.length,
        totalStudents: studentsRes.data.length,
        totalCertificates: 0,
        recentActivity: []
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Total Cursos',
      value: stats.totalCourses,
      icon: GraduationCap,
      color: 'border-l-[#004E89]',
      testId: 'dashboard-stat-courses'
    },
    {
      title: 'Total Alumnos',
      value: stats.totalStudents,
      icon: Users,
      color: 'border-l-[#0EA5E9]',
      testId: 'dashboard-stat-students'
    },
    {
      title: 'Certificados Emitidos',
      value: stats.totalCertificates,
      icon: Award,
      color: 'border-l-[#10B981]',
      testId: 'dashboard-stat-certificates'
    }
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004E89]"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-8" data-testid="dashboard-container">
        {/* Header */}
        <div>
          <h1 className="font-manrope font-bold text-4xl text-slate-900 mb-2" data-testid="dashboard-title">
            Dashboard
          </h1>
          <p className="font-inter text-slate-600">
            Bienvenido de nuevo, {user?.nombre}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <div
                key={stat.title}
                data-testid={stat.testId}
                className={`bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow p-6 border-l-4 ${stat.color}`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="p-2 bg-slate-50 rounded-lg">
                    <Icon className="w-6 h-6 text-[#004E89]" />
                  </div>
                  <TrendingUp className="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-600 mb-1">{stat.title}</p>
                  <p className="text-3xl font-bold text-slate-900">{stat.value}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Welcome Banner */}
        <div
          className="relative rounded-lg overflow-hidden h-64 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.pexels.com/photos/5428151/pexels-photo-5428151.jpeg)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#004E89]/90 to-[#004E89]/70"></div>
          <div className="relative z-10 h-full flex flex-col justify-center p-8">
            <h2 className="font-manrope font-bold text-3xl text-white mb-4">
              Sistema de Gestión de Formación
            </h2>
            <p className="font-inter text-lg text-white max-w-2xl">
              Administra cursos, gestiona alumnos y genera certificados oficiales con código de verificación seguro.
            </p>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
          <h3 className="font-manrope font-semibold text-xl text-slate-900 mb-4">Acciones Rápidas</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <a
              href="/courses"
              data-testid="dashboard-quick-action-courses"
              className="p-4 border border-slate-200 rounded-lg hover:border-[#004E89] hover:bg-slate-50 transition-colors"
            >
              <GraduationCap className="w-8 h-8 text-[#004E89] mb-2" />
              <p className="font-medium text-slate-900">Gestionar Cursos</p>
              <p className="text-sm text-slate-600 mt-1">Crear y editar cursos de formación</p>
            </a>
            <a
              href="/students"
              data-testid="dashboard-quick-action-students"
              className="p-4 border border-slate-200 rounded-lg hover:border-[#004E89] hover:bg-slate-50 transition-colors"
            >
              <Users className="w-8 h-8 text-[#004E89] mb-2" />
              <p className="font-medium text-slate-900">Gestionar Alumnos</p>
              <p className="text-sm text-slate-600 mt-1">Registrar y administrar alumnos</p>
            </a>
            <a
              href="/courses"
              data-testid="dashboard-quick-action-certificates"
              className="p-4 border border-slate-200 rounded-lg hover:border-[#004E89] hover:bg-slate-50 transition-colors"
            >
              <Award className="w-8 h-8 text-[#004E89] mb-2" />
              <p className="font-medium text-slate-900">Generar Certificados</p>
              <p className="text-sm text-slate-600 mt-1">Emitir certificados oficiales</p>
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;